import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';

import { CATEGORY_NAME_TO_I18N_KEY } from '../../constants/categories';
import { useParameterCategories } from '../../hooks/useParameterCategories';
import type {
  ConfigurationPlatform,
  ParameterCategory,
  ParameterSection,
} from '../../types/configuration.types';

interface ParameterCategoryFilterProps {
  /** Platform whose category catalog is loaded (drives labels + counts). */
  platform: ConfigurationPlatform;
  /** Extra classes on the trigger (e.g. responsive visibility). */
  className?: string;
}

/** Section order mirrors the desktop rail: Experiment first, then Production. */
const SECTION_ORDER: ParameterSection[] = ['experiment', 'production'];

/**
 * Compact, searchable replacement for the CategoryRail shown below `xl`
 * (<1280px), inline with the Platform dropdown. Reads/writes the same
 * `category` URL param as the rail so the two selection surfaces stay in
 * sync; the trigger surfaces the active selection plus its parameter count.
 */
export function ParameterCategoryFilter({ platform, className }: ParameterCategoryFilterProps) {
  const { t } = useTranslation('configuration');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Read loosely — this renders in the manager layout (above the parameters
  // route that owns the `category` param), so a strict `from` isn't available.
  const search = useSearch({ strict: false });
  const selected = typeof search.category === 'string' ? search.category : 'all';

  const [open, setOpen] = useState(false);

  const { data: categories = [] } = useParameterCategories({ platform });

  /** Resolves a backend category name to its translated label. */
  const labelFor = (c: ParameterCategory) =>
    t(CATEGORY_NAME_TO_I18N_KEY[c.name] ?? '', { defaultValue: c.name });

  // Counts come straight from the catalog's backend `parameterCount`; the
  // "All" total is their sum so it matches the rail's aggregate.
  const total = useMemo(
    () => categories.reduce((sum, c) => sum + c.parameterCount, 0),
    [categories],
  );

  const selectedCategory = categories.find((c) => c.id === selected) ?? null;
  const triggerLabel = selectedCategory ? labelFor(selectedCategory) : t('allParameters');
  const triggerCount = selectedCategory ? selectedCategory.parameterCount : total;

  /** Persist the pick in the URL (replace) so it survives refresh + tab switches. */
  const handleSelect = (next: string) => {
    void navigate({ to: pathname, search: { ...search, category: next }, replace: true });
    setOpen(false);
  };

  const renderSection = (section: ParameterSection) => {
    const items = categories.filter((c) => c.section === section);
    if (items.length === 0) return null;
    return (
      <CommandGroup key={section} heading={t(`sections.${section}`)}>
        {items.map((c) => (
          <CategoryItem
            key={c.id}
            label={labelFor(c)}
            count={c.parameterCount}
            active={selected === c.id}
            onSelect={() => {
              handleSelect(c.id);
            }}
          />
        ))}
      </CommandGroup>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={t('categoryFilter.label')}
          className={cn('h-10 min-w-52 cursor-pointer justify-between font-normal', className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{triggerLabel}</span>
            <CountBadge count={triggerCount} />
          </span>
          <ChevronsUpDown className="text-muted-foreground ml-2 h-4 w-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <Command>
          <CommandInput placeholder={t('categoryFilter.searchPlaceholder')} />
          <CommandList>
            <CommandEmpty>{t('categoryFilter.empty')}</CommandEmpty>
            <CommandGroup>
              <CategoryItem
                label={t('allParameters')}
                count={total}
                active={selected === 'all'}
                onSelect={() => {
                  handleSelect('all');
                }}
              />
            </CommandGroup>
            {SECTION_ORDER.map(renderSection)}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface CategoryItemProps {
  label: string;
  count: number;
  active: boolean;
  onSelect: () => void;
}

/** Single dropdown row: check + label on the left, count badge on the right. */
function CategoryItem({ label, count, active, onSelect }: CategoryItemProps) {
  return (
    <CommandItem value={label} onSelect={onSelect} className="cursor-pointer justify-between">
      <span className="flex min-w-0 items-center">
        <Check className={cn('mr-2 h-4 w-4 shrink-0', active ? 'opacity-100' : 'opacity-0')} />
        <span className="truncate">{label}</span>
      </span>
      <CountBadge count={count} />
    </CommandItem>
  );
}

/** Muted pill showing a parameter count, matching the rail's badge style. */
function CountBadge({ count }: { count: number }) {
  return (
    <span className="bg-muted text-foreground ml-2 inline-flex min-w-[1.5rem] shrink-0 items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-medium">
      {count}
    </span>
  );
}
